import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import { useUpdateQuestionnaireMutation } from "@/pages/deals/hooks";
import { QuestionnaireBaseOutput } from "@/pages/deals/types";

const TRANSLATION_PREFIX =
  "settings.deals.workflow.workflowDetailedView.pocSuccessCriteria";

interface Props {
  questionnaireId?: string;
  questionnaire?: QuestionnaireBaseOutput | null;
  onQuestionnaireUpdate?: () => Promise<any>;
}

const POCSuccessCriteriaSection: React.FC<Props> = ({
  questionnaireId,
  questionnaire,
  onQuestionnaireUpdate
}) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const updateQuestionnaireMutation = useUpdateQuestionnaireMutation();

  useEffect(() => {
    if (questionnaire) {
      setIsEnabled(questionnaire?.isSuccessCriteriaEnabled || false);
    }
  }, [questionnaire]);

  const handleSwitchChange = async (checked: boolean) => {
    if (!questionnaireId) {
      console.error("Cannot update guardrails: questionnaireId is missing");
      return;
    }

    setIsUpdating(true);

    try {
      await updateQuestionnaireMutation.mutateAsync({
        pathParams: { questionnaireId },
        data: { isSuccessCriteriaEnabled: checked }
      });
      setIsEnabled(checked);
      if (onQuestionnaireUpdate) {
        await onQuestionnaireUpdate();
      }
    } catch (error) {
      console.error("Failed to update questionnaire guardrails:", error);
      setIsEnabled(!checked);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-border-border1 bg-surface-widget-card p-4">
      <div className="flex flex-col gap-1 flex-1">
        <Text variant="body">
          {t(`${TRANSLATION_PREFIX}.sectionTitle`, {
            workflowName: questionnaire?.name || "POC"
          })}
        </Text>
        <Text variant="bodySecondary">
          {t(`${TRANSLATION_PREFIX}.sectionSubtitle`)}
        </Text>
      </div>
      <Switch
        checked={isEnabled}
        onCheckedChange={handleSwitchChange}
        disabled={isUpdating}
      />
    </div>
  );
};

export default POCSuccessCriteriaSection;
